import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { StopwatchIcon } from '@sorare/core/src/atoms/icons/StopwatchIcon/index.tsx';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';

import { HeartsDialog } from '#src/components/board/HeartsDialog/index.tsx';

import { Lifes } from '../Lifes/index.tsx';
import { ClockDialogContent } from './ClockDialogContent/index.tsx';
import type { BoardTimer_thresholdPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: BoardTimer_thresholdPickerTask;
};

const HeartsButton = styled(ButtonBase)`
  display: flex;
  gap: var(--half-unit);
`;

const ClockButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LifesContainer = styled(Horizontal)`
  border: 1px solid transparent;
  padding: 0 var(--unit) var(--half-unit) var(--intermediate-unit);
  margin-left: calc(var(--double-unit) * -1);
  gap: var(--half-unit);
  background:
    linear-gradient(to right, var(--c-black), var(--c-black)) padding-box,
    linear-gradient(
        to top,
        rgba(255, 255, 255, 0.17),
        rgba(255, 255, 255, 0.17) 62.98%,
        rgba(255, 255, 255, 0.7)
      )
      border-box;
  align-items: center;
  transform: skew(-20deg);

  & > * {
    transform: skew(20deg);
  }
`;

export const BoardTimer = ({ task }: Props) => {
  const [heartsDialogOpen, setHeartsDialogOpen] = useState(false);
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  if (!task.expiresAt && !task.wouldExpireAt) {
    return (
      <>
        <HeartsButton onClick={() => setHeartsDialogOpen(true)}>
          <Lifes task={task} size={2} />
        </HeartsButton>
        <HeartsDialog
          open={heartsDialogOpen}
          onClose={() => setHeartsDialogOpen(false)}
        />
      </>
    );
  }
  return (
    <ClockButton onClick={() => setClockDialogOpen(true)}>
      <StopwatchIcon
        style={{ zIndex: 1 }}
        timeProgress={
          task.expiresAt ? task.expirationProgressBasisPoints : null
        }
        size={6}
      />
      <LifesContainer>
        <Lifes task={task} size={1.5} />
      </LifesContainer>
      <Dialog
        open={clockDialogOpen}
        onClose={() => setClockDialogOpen(false)}
        maxWidth="xs"
        hideHeader
        fullScreen={false}
      >
        <ClockDialogContent
          task={task}
          onClose={() => setClockDialogOpen(false)}
        />
      </Dialog>
    </ClockButton>
  );
};

BoardTimer.fragments = {
  thresholdPickerTask: gql`
    fragment BoardTimer_thresholdPickerTask on ThresholdPickerTask {
      id
      ...ClockDialogContent_thresholdPickerTask
      ...Lifes_thresholdPickerTask
    }
    ${ClockDialogContent.fragments.thresholdPickerTask}
    ${Lifes.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<BoardTimer_thresholdPickerTask>,
};
